import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Building2, ImageUp, Lock, Palette } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, PageLoading } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { INDUSTRIES } from "@/lib/industries";

const COLOR_PRESETS = ["#0A192F", "#1E3A8A", "#0F766E", "#B91C1C", "#C2410C", "#6D28D9", "#0369A1", "#111827"];

export default function Company() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const [branding, setBranding] = useState(false);
  const [whiteLabel, setWhiteLabel] = useState(false);
  const [color, setColor] = useState("#0A192F");
  const [savingColor, setSavingColor] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const canEdit = ["COMPANY_OWNER", "COMPANY_ADMIN"].includes(user?.role);

  const loadLogo = async () => {
    try {
      const r = await api.get("/employer/company/logo", { responseType: "blob" });
      setLogoUrl(URL.createObjectURL(r.data));
    } catch { setLogoUrl(null); }
  };

  useEffect(() => {
    api.get("/employer/company").then((r) => { setData(r.data); setF(r.data.company); if (r.data.company?.brand_color) setColor(r.data.company.brand_color); });
    api.get("/employer/subscription").then((r) => {
      const on = !!r.data?.plan?.branding_enabled;
      setBranding(on);
      setWhiteLabel(r.data?.plan?.tier === "unlimited");
      if (on) loadLogo();
    }).catch(() => {});
  }, []);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/employer/company", f); toast.success(t("cop.updated")); refresh(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const onLogoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post("/employer/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("cop.logoUpdated"));
      await loadLogo();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const saveColor = async () => {
    setSavingColor(true);
    try { await api.put("/employer/company/brand-color", { color }); toast.success(t("cop.colorUpdated")); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSavingColor(false); }
  };

  if (!data) return <><PageHeader title={t("cop.title")} /><PageLoading /></>;

  return (
    <div data-testid="company-page">
      <PageHeader title={t("cop.title")} subtitle={t("cop.subtitle")} />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="card-soft p-6 lg:col-span-2">
          <div className="mb-4 flex items-center gap-2 font-heading font-bold text-navy"><Building2 className="h-4 w-4 text-cyan-dark" /> {t("cop.details")}</div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2"><Label>{t("cop.name")}</Label><Input value={f.name || ""} onChange={set("name")} disabled={!canEdit} data-testid="company-name" /></div>
            <div className="space-y-2"><Label>{t("cop.website")}</Label><Input value={f.website || ""} onChange={set("website")} disabled={!canEdit} /></div>
            <div className="space-y-2"><Label>{t("cop.industry")}</Label>
              <select value={f.industry || ""} onChange={(ev) => set("industry")(ev)} disabled={!canEdit} data-testid="company-industry" className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm disabled:opacity-60">
                <option value="">—</option>
                {INDUSTRIES.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
              </select>
            </div>
            <div className="space-y-2"><Label>{t("cop.size")}</Label><Input value={f.company_size || ""} onChange={set("company_size")} disabled={!canEdit} /></div>
            <div className="space-y-2"><Label>{t("cop.intl")}</Label><Input value={f.international_employee_count || ""} onChange={set("international_employee_count")} disabled={!canEdit} /></div>
            <div className="space-y-2"><Label>{t("cop.country")}</Label><Input value={f.country || ""} onChange={set("country")} disabled={!canEdit} /></div>
            <div className="space-y-2"><Label>{t("guide.cityField")}</Label><Input value={f.city || ""} onChange={set("city")} disabled={!canEdit} data-testid="company-city" /></div>
            <div className="space-y-2 sm:col-span-2"><Label>{t("guide.officeField")}</Label><Input value={f.auslaenderbehoerde || ""} onChange={set("auslaenderbehoerde")} disabled={!canEdit} placeholder="Ausländerbehörde …" data-testid="company-auslaenderbehoerde" /><p className="text-xs text-slate-400">{t("guide.officeFieldHint")}</p></div>
          </div>
          {canEdit && <div className="mt-6 flex justify-end"><Button onClick={save} disabled={saving} className="rounded-full bg-navy text-white" data-testid="save-company-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("cop.save")}</Button></div>}
        </div>

        <div className="space-y-6">
          <div className="card-soft p-6" data-testid="company-logo-card">
            <div className="mb-1 flex items-center gap-2 font-heading font-bold text-navy"><ImageUp className="h-4 w-4 text-cyan-dark" /> {t("cop.logoTitle")}</div>
            <p className="mb-4 text-xs text-slate-400">{t("cop.logoSub")}</p>
            {branding ? (
              <>
                <div className="mb-4 flex h-28 items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 p-3">
                  {logoUrl
                    ? <img src={logoUrl} alt="Company logo" className="max-h-full max-w-full object-contain" data-testid="company-logo-preview" />
                    : <span className="text-xs text-slate-400">{t("cop.logoNone")}</span>}
                </div>
                <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" onChange={onLogoPick} data-testid="company-logo-input" />
                {canEdit && (
                  <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full rounded-full bg-navy text-white" data-testid="company-logo-upload-btn">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : (logoUrl ? t("cop.logoReplace") : t("cop.logoUpload"))}
                  </Button>
                )}
                <p className="mt-3 text-xs text-slate-400">{t("cop.logoHint")}</p>
              </>
            ) : (
              <div className="flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-xs text-amber-700" data-testid="company-logo-locked">
                <Lock className="mt-0.5 h-4 w-4 shrink-0" /> {t("cop.logoLocked")}
              </div>
            )}
          </div>

          <div className="card-soft p-6" data-testid="company-color-card">
            <div className="mb-1 flex items-center gap-2 font-heading font-bold text-navy"><Palette className="h-4 w-4 text-cyan-dark" /> {t("cop.colorTitle")}</div>
            <p className="mb-4 text-xs text-slate-400">{t("cop.colorSub")}</p>
            {whiteLabel ? (
              <>
                <div className="mb-4 h-16 rounded-xl border border-slate-200" style={{ backgroundColor: color }} data-testid="company-color-preview" />
                <div className="mb-4 flex flex-wrap gap-2" data-testid="company-color-presets">
                  {COLOR_PRESETS.map((c) => (
                    <button key={c} type="button" onClick={() => canEdit && setColor(c)} disabled={!canEdit}
                      title={c} data-testid={`color-preset-${c.slice(1)}`}
                      className={`h-8 w-8 rounded-full border-2 transition-transform hover:scale-110 ${color === c ? "border-navy ring-2 ring-cyan" : "border-white shadow"}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <input type="color" value={color} onChange={(e) => setColor(e.target.value.toUpperCase())} disabled={!canEdit} className="h-10 w-14 cursor-pointer rounded-md border border-slate-200 bg-white p-1" data-testid="company-color-input" />
                  <Input value={color} onChange={(e) => setColor(e.target.value.toUpperCase())} disabled={!canEdit} className="font-mono" data-testid="company-color-hex" />
                </div>
                {canEdit && (
                  <div className="mt-4 flex gap-2">
                    <Button onClick={saveColor} disabled={savingColor} className="flex-1 rounded-full bg-navy text-white" data-testid="company-color-save-btn">
                      {savingColor ? <Loader2 className="h-4 w-4 animate-spin" /> : t("cop.colorSave")}
                    </Button>
                    <Button variant="outline" onClick={() => setColor("#0A192F")} disabled={savingColor} className="rounded-full" data-testid="company-color-reset-btn">{t("cop.colorReset")}</Button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-xs text-amber-700" data-testid="company-color-locked">
                <Lock className="mt-0.5 h-4 w-4 shrink-0" /> {t("cop.colorLocked")}
              </div>
            )}
          </div>

          <div className="card-soft p-6">
            <div className="mb-4 font-heading font-bold text-navy">{t("cop.team")} ({data.team.length})</div>
            <div className="space-y-3">
              {data.team.map((u) => (
                <div key={u.id} className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-navy text-[11px] font-bold text-white">{(u.first_name?.[0] || "") + (u.last_name?.[0] || "")}</span>
                  <div className="min-w-0 flex-1"><div className="truncate text-sm font-semibold text-navy">{u.first_name} {u.last_name}</div><div className="truncate text-xs text-slate-400">{u.email}</div></div>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{u.role.replace("_", " ")}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
